import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { NotebookText, Image as ImageIcon, Loader2, Pencil, Trash2, Send, X, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { apiService } from '@/services/api'
import { useMediaUpload } from '@/hooks/useMediaUpload'
import type { MediaFile } from '@/types/message'

export interface Note {
  id: string
  tableId: string
  content: string
  mediaFileId: string | null
  mediaUrl: string | null
  mediaType: string | null
  createdAt: string
  updatedAt: string | null
}

interface NotesPanelProps {
  tableId: string
}

function formatNoteDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
    ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

export function NotesPanel({ tableId }: NotesPanelProps) {
  const [notes, setNotes] = useState<Note[]>([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')
  const [attachment, setAttachment] = useState<MediaFile | null>(null)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')

  const fileInputRef = useRef<HTMLInputElement>(null)
  const { uploadMedia, isUploading, progress } = useMediaUpload()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    apiService.request<Note[]>(`/tables/${tableId}/notes`)
      .then(data => { if (!cancelled) setNotes(data) })
      .catch(err => {
        console.error('Erro ao carregar anotações:', err)
        if (!cancelled) toast.error('Erro ao carregar anotações')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [tableId])

  const handleAttach = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo: 10MB')
      return
    }
    try {
      const media = await uploadMedia(file, {
        category: 'message',
        entityId: `note-${Date.now()}`,
        tableId,
        isShared: false // privada: só o dono vê (aba Minhas mídias)
      })
      setAttachment(media)
    } catch (err) {
      console.error('Erro no upload da anotação:', err)
      toast.error('Erro ao enviar imagem. Tente novamente.')
    }
  }, [uploadMedia, tableId])

  const handleCreate = useCallback(async () => {
    if (!draft.trim() && !attachment) return
    setSaving(true)
    try {
      const created = await apiService.request<Note>('/notes', {
        method: 'POST',
        body: JSON.stringify({
          table_id: tableId,
          content: draft,
          media_file_id: attachment?.id ?? null
        })
      })
      setNotes(prev => [created, ...prev])
      setDraft('')
      setAttachment(null)
    } catch (err) {
      console.error('Erro ao salvar anotação:', err)
      toast.error('Erro ao salvar anotação')
    } finally {
      setSaving(false)
    }
  }, [draft, attachment, tableId])

  const handleSaveEdit = useCallback(async (note: Note) => {
    if (!editDraft.trim() && !note.mediaFileId) {
      toast.error('A anotação não pode ficar vazia')
      return
    }
    try {
      const updated = await apiService.request<Note>(`/notes/${note.id}`, {
        method: 'PUT',
        body: JSON.stringify({ content: editDraft })
      })
      setNotes(prev => prev.map(n => n.id === note.id ? updated : n))
      setEditingId(null)
    } catch (err) {
      console.error('Erro ao editar anotação:', err)
      toast.error('Erro ao editar anotação')
    }
  }, [editDraft])

  const handleDelete = useCallback(async (noteId: string) => {
    try {
      await apiService.request(`/notes/${noteId}`, { method: 'DELETE' })
      setNotes(prev => prev.filter(n => n.id !== noteId))
    } catch (err) {
      console.error('Erro ao apagar anotação:', err)
      toast.error('Erro ao apagar anotação')
    }
  }, [])

  return (
    <div className="h-full bg-game-secondary dark:bg-zinc-900/95 flex flex-col">
      <div className="p-4 border-b border-game-border-primary dark:border-zinc-800">
        <p className="text-xs uppercase tracking-[0.2em] text-game-muted dark:text-zinc-500 mb-1">Anotações</p>
        <h3 className="font-semibold text-lg text-game-primary dark:text-zinc-100">Seus registros nesta mesa</h3>
        <p className="text-xs text-game-muted dark:text-zinc-400 mt-1">
          Privadas: só você vê. Textos e imagens para consulta posterior.
        </p>
      </div>

      {/* Composer */}
      <div className="p-4 border-b border-game-border-primary dark:border-zinc-800 space-y-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Escreva uma anotação..."
          rows={2}
          className="resize-none rounded-xl bg-white dark:bg-zinc-800/80 border-slate-200 dark:border-zinc-700/50 text-game-primary dark:text-zinc-100 placeholder:text-game-muted dark:placeholder:text-zinc-500 focus-visible:ring-violet-500/30"
        />

        {isUploading && (
          <div className="flex items-center gap-2 text-violet-600 dark:text-violet-300 text-xs">
            <Loader2 size={12} className="animate-spin" />
            <span>Enviando imagem... {Math.round(progress)}%</span>
          </div>
        )}

        {attachment && (
          <div className="relative inline-block">
            <img
              src={attachment.url}
              alt={attachment.filename}
              className="h-20 rounded-lg border border-game-border-primary dark:border-zinc-700 object-cover"
            />
            <button
              onClick={() => setAttachment(null)}
              className="absolute -top-2 -right-2 w-5 h-5 bg-rose-500 hover:bg-rose-400 rounded-full flex items-center justify-center shadow"
              title="Remover imagem"
            >
              <X className="h-3 w-3 text-white" />
            </button>
          </div>
        )}

        <div className="flex items-center justify-between">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAttach}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || !!attachment}
            className="gap-1.5 text-game-muted hover:text-game-primary dark:hover:text-zinc-200"
            title="Anexar imagem"
          >
            <ImageIcon className="h-4 w-4" />
            Imagem
          </Button>
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={saving || isUploading || (!draft.trim() && !attachment)}
            className="gap-1.5 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Salvar
          </Button>
        </div>
      </div>

      {/* Lista */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
            </div>
          )}

          {!loading && notes.length === 0 && (
            <div className="text-center py-12 px-4 text-game-muted">
              <div className="w-12 h-12 mx-auto bg-game-tertiary dark:bg-zinc-800/50 rounded-full flex items-center justify-center mb-3">
                <NotebookText className="h-6 w-6 text-game-muted" />
              </div>
              <p className="text-sm">Nenhuma anotação ainda</p>
              <p className="text-xs mt-1">Registre descobertas, pistas e lembretes da aventura.</p>
            </div>
          )}

          {notes.map((note) => {
            const isEditing = editingId === note.id
            return (
              <div
                key={note.id}
                className="group rounded-xl border border-game-border-primary dark:border-zinc-800 bg-white dark:bg-zinc-800/40 p-3"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-game-muted dark:text-zinc-500">
                    {formatNoteDate(note.createdAt)}
                    {note.updatedAt && ' · editada'}
                  </span>
                  {!isEditing && (
                    <div className="flex gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => { setEditingId(note.id); setEditDraft(note.content) }}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-game-muted hover:text-game-primary dark:hover:text-zinc-200 hover:bg-game-hover dark:hover:bg-zinc-700/50"
                        title="Editar"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(note.id)}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-game-muted hover:text-rose-400 hover:bg-rose-500/10"
                        title="Apagar"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {note.mediaUrl && (
                  <img
                    src={note.mediaUrl}
                    alt="Imagem da anotação"
                    className="max-h-48 rounded-lg border border-game-border-primary dark:border-zinc-700 mb-2 object-contain"
                  />
                )}

                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={3}
                      autoFocus
                      className="resize-none rounded-lg bg-white dark:bg-zinc-800/80 border-slate-200 dark:border-zinc-700/50 text-game-primary dark:text-zinc-100"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingId(null)}
                        className="gap-1 text-game-muted"
                      >
                        <X className="h-3.5 w-3.5" /> Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSaveEdit(note)}
                        className="gap-1 bg-violet-600 hover:bg-violet-500 text-white"
                      >
                        <Check className="h-3.5 w-3.5" /> Salvar
                      </Button>
                    </div>
                  </div>
                ) : (
                  note.content && (
                    <p className={cn('text-sm text-game-primary dark:text-zinc-100 whitespace-pre-wrap break-words')}>
                      {note.content}
                    </p>
                  )
                )}
              </div>
            )
          })}
        </div>
      </ScrollArea>
    </div>
  )
}
